import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ThemeProvider } from "@/context/ThemeContext";
import { ChatbotProvider } from "@/context/ChatbotContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { PushNotificationProvider } from "@/context/PushNotificationContext";

// Scroll to Top Component - scrolls to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Admin Components
import Sidebar from "@/components/layout/Sidebar";
import Header from "@/components/layout/Header";
import Dashboard from "@/pages/Dashboard";
import AllJobs from "@/pages/AllJobs";
import AppliedJobs from "@/pages/AppliedJobs";
import AddNewJob from "@/pages/AddNewJob";
import RemovedJobs from "@/pages/RemovedJobs";
import Enquiries from "@/pages/Enquiries";
import Projects from "@/pages/Projects";
import AddProject from "@/pages/AddProject";
import Blogs from "@/pages/Blogs";
import LiveChat from "@/pages/LiveChat";
import UserManagement from "@/pages/UserManagement";
import SuperAdminUserManagement from "@/pages/SuperAdminUserManagement";
import TalentManagement from "@/pages/TalentManagement";
import ClientManagement from "@/pages/ClientManagement";
import TenantBrandingPage from "@/pages/admin/TenantBrandingPage";

// Website Components
import Website from "@/pages/website/Website";
import CareersPage from "@/pages/website/CareersPage";
import BlogsPage from "@/pages/website/BlogsPage";
import OurWorkPage from "@/pages/website/OurWorkPage";
import ClientsPage from "@/pages/website/ClientsPage";
import ServicePage from "@/pages/website/ServicePage";
import PolicyPage from "@/pages/website/PolicyPage";
import TalentPoolPage from "@/pages/website/TalentPoolPage";
import JoinTalentPool from "@/pages/website/JoinTalentPool";
import ProductComingSoon from "@/pages/website/ProductComingSoon";
import AINewsPage from "@/pages/website/AINewsPage";
import SalesDeck from "@/pages/website/SalesDeck";

// Auth Components
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ActivateAccountPage from "@/pages/ActivateAccountPage";
import PortalPage from "@/pages/PortalPage";
import HRPortalEmbed from "@/pages/HRPortalEmbed";
import Analytics from "@/pages/Analytics";

// Enterprise Suite Components
import EnterpriseSuiteLayout from "@/pages/suite/EnterpriseSuiteLayout";
import ProductEmbed from "@/pages/suite/ProductEmbed";

// Sales Module Components
import SalesLayout from "@/components/sales/SalesLayout";
import SalesDashboard from "@/pages/sales/SalesDashboard";
import SalesPlatformAdmin from "@/pages/sales/SalesPlatformAdmin";
import ContactsPage from "@/pages/sales/ContactsPage";
import LeadsPage from "@/pages/sales/LeadsPage";
import TasksPage from "@/pages/sales/TasksPage";
import InterviewsPage from "@/pages/sales/InterviewsPage";
import ContractsPage from "@/pages/sales/ContractsPage";
import RemindersPage from "@/pages/sales/RemindersPage";
import ReportsPage from "@/pages/sales/ReportsPage";
import AIFeaturesPage from "@/pages/sales/AIFeaturesPage";
import TeamManagementPage from "@/pages/sales/TeamManagementPage";
import TeamLocatorPage from "@/pages/sales/TeamLocatorPage";
import MobileTrackerPage from "@/pages/sales/MobileTrackerPage";
import DataImportPage from "@/pages/sales/DataImportPage";
import RevenueAnalyticsPage from "@/pages/sales/RevenueAnalyticsPage";

// Finance Module Components
import FinanceLayout from "@/components/finance/FinanceLayout";
import FinanceDashboard from "@/pages/finance/Dashboard";
import FinanceInvoices from "@/pages/finance/Invoices";
import FinanceCreateInvoice from "@/pages/finance/CreateInvoice";
import FinanceRecurringInvoices from "@/pages/finance/RecurringInvoices";
import FinanceEstimates from "@/pages/finance/Estimates";
import FinanceCustomers from "@/pages/finance/Customers";
import FinanceEInvoice from "@/pages/finance/EInvoice";
import FinanceExpenses from "@/pages/finance/Expenses";
import FinanceBills from "@/pages/finance/Bills";
import FinanceVendors from "@/pages/finance/Vendors";
import FinancePurchaseOrders from "@/pages/finance/PurchaseOrders";
import FinancePayroll from "@/pages/finance/Payroll";
import FinanceProjects from "@/pages/finance/Projects";
import FinanceMileage from "@/pages/finance/Mileage";
import FinanceReports from "@/pages/finance/Reports";
import FinanceAIAssistant from "@/pages/finance/AIAssistant";
import FinanceAITools from "@/pages/finance/AITools";
import FinanceUsers from "@/pages/finance/Users";
import FinanceSettings from "@/pages/finance/Settings";
import FinanceImportData from "@/pages/finance/ImportData";

// HR Module Components
import HRLayout from "@/components/hr/HRLayout";
import HRDashboard from "@/pages/hr/HRDashboard";
import HRDashboardWrapper from "@/pages/hr/HRDashboardWrapper";
import EmployeeSelfService from "@/pages/hr/EmployeeSelfService";
import OfferTemplates from "@/pages/hr/OfferTemplates";
import OnboardingDashboard from "@/pages/hr/OnboardingDashboard";
import HREmployees from "@/pages/hr/Employees";
import EmployeeDetails from "@/pages/hr/EmployeeDetails";
import EmployeeEdit from "@/pages/hr/EmployeeEdit";
import HRLeaveManagement from "@/pages/hr/LeaveManagement";
import HRAttendance from "@/pages/hr/Attendance";
import HRClaims from "@/pages/hr/Claims";
import HRPayroll from "@/pages/hr/Payroll";
import HRPayslips from "@/pages/hr/Payslips";
import HRPerformance from "@/pages/hr/Performance";
import HRReports from "@/pages/hr/Reports";
import HRForms from "@/pages/hr/Forms";
import HRImportData from "@/pages/hr/ImportData";
import HRSettings from "@/pages/hr/Settings";
import HRDocuments from "@/pages/hr/Documents";
import SignDocument from "@/pages/hr/SignDocument";
import PublicSign from "@/pages/hr/PublicSign";
import MyProfile from "@/pages/hr/MyProfile";

// Onboarding / Signing
import OnboardingInterviews from "@/pages/sales/OnboardingInterviews";
import SignOfferPage from "@/pages/SignOfferPage";

// Docs Module Components
import DocsLayout from "@/components/docs/DocsLayout";
import DocsDashboard from "@/pages/docs/DocsDashboard";
import DocsChatPage from "@/pages/docs/ChatPage";
import DocsDocumentsPage from "@/pages/docs/DocumentsPage";
import DocsComparePage from "@/pages/docs/DocumentComparePage";
import DocsBatchAnalysisPage from "@/pages/docs/BatchAnalysisPage";
import DocsReportsPage from "@/pages/docs/ReportsPage";
import DocsUserManagementPage from "@/pages/docs/UserManagementPage";
import DocsAuditLogPage from "@/pages/docs/AuditLogPage";
import DocsSSOConfigPage from "@/pages/docs/SSOConfigPage";
import DocsLicenseManagementPage from "@/pages/docs/LicenseManagementPage";
import DocsQueryManagementPage from "@/pages/docs/QueryManagementPage";

// Global Components
import Chatbot from "@/components/Chatbot";
import { SEOHead } from "@/components/SEOHead";

import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create context for sidebar state
export const SidebarContext = createContext();

import { useTheme, adminTheme } from "@/context/ThemeContext";

export const useSidebar = () => useContext(SidebarContext);

function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { isDark } = useTheme();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <SidebarContext.Provider value={{ sidebarOpen, setSidebarOpen, isMobile }}>
      <div className={`min-h-screen transition-colors duration-300 ${adminTheme.pageBg(isDark)}`}>
        <Sidebar />
        <div className={`transition-all duration-300 ${sidebarOpen && !isMobile ? 'lg:ml-64' : 'lg:ml-20'}`}>
          <Header />
          <main className="p-4 lg:p-6">
            {children}
          </main>
        </div>
      </div>
    </SidebarContext.Provider>
  );
}

function App() {
  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`);
        if (response.data.total_jobs === 0) {
          await axios.post(`${API}/seed`);
          window.location.reload();
        }
      } catch (e) {
        console.log("Seeding data...");
        try {
          await axios.post(`${API}/seed`);
        } catch (err) {
          console.error("Seed error:", err);
        }
      }
    };
    seedData();
  }, []);

  return (
    <BrandingProvider>
      <ThemeProvider>
        <ChatbotProvider>
          <PushNotificationProvider>
            <div className="App">
              <BrowserRouter>
                <ScrollToTop />
                <SEOHead />
                <Routes>
              {/* Public Website Routes */}
              <Route path="/" element={<Website />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/blogs" element={<BlogsPage />} />
              <Route path="/our-work" element={<OurWorkPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/services/:serviceId" element={<ServicePage />} />
              <Route path="/ai-talent" element={<TalentPoolPage />} />
              <Route path="/ai-news" element={<AINewsPage />} />
              <Route path="/join-talent-pool" element={<JoinTalentPool />} />
              <Route path="/products/:productId" element={<ProductComingSoon />} />
              
              {/* Mobile Location Tracker - Public URL for sales team phones */}
              <Route path="/track" element={<MobileTrackerPage />} />
              
              {/* Auth Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/activate/:token" element={<ActivateAccountPage />} />
              <Route path="/portal" element={<PortalPage />} />
              <Route path="/super-admin/users" element={<SuperAdminUserManagement />} />
              
              {/* HR Portal (MyHR Orbit Embed) - Legacy */}
              <Route path="/hr-portal" element={<HRPortalEmbed />} />
              
              {/* Enterprise Suite Routes - Iframe embeds for other products */}
              <Route path="/suite" element={<EnterpriseSuiteLayout />}>
                <Route path=":productId" element={<ProductEmbed />} />
              </Route>
              
              {/* MySales Orbit - Fully Integrated */}
              <Route path="/suite/sales" element={<SalesLayout />}>
                <Route index element={<SalesDashboard />} />
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="interviews" element={<InterviewsPage />} />
                <Route path="onboarding" element={<OnboardingInterviews />} />
                <Route path="contracts" element={<ContractsPage />} />
                <Route path="reminders" element={<RemindersPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="ai" element={<AIFeaturesPage />} />
                <Route path="team" element={<TeamManagementPage />} />
                <Route path="team/locate" element={<TeamLocatorPage />} />
                <Route path="import" element={<DataImportPage />} />
                <Route path="revenue" element={<RevenueAnalyticsPage />} />
                <Route path="platform-admin" element={<SalesPlatformAdmin />} />
              </Route>
              
              {/* MyFinance Orbit - Fully Integrated */}
              <Route path="/suite/finance" element={<FinanceLayout />}>
                <Route index element={<FinanceDashboard />} />
                <Route path="invoices" element={<FinanceInvoices />} />
                <Route path="invoices/new" element={<FinanceCreateInvoice />} />
                <Route path="recurring-invoices" element={<FinanceRecurringInvoices />} />
                <Route path="estimates" element={<FinanceEstimates />} />
                <Route path="customers" element={<FinanceCustomers />} />
                <Route path="e-invoice" element={<FinanceEInvoice />} />
                <Route path="expenses" element={<FinanceExpenses />} />
                <Route path="bills" element={<FinanceBills />} />
                <Route path="vendors" element={<FinanceVendors />} />
                <Route path="purchase-orders" element={<FinancePurchaseOrders />} />
                <Route path="payroll" element={<FinancePayroll />} />
                <Route path="projects" element={<FinanceProjects />} />
                <Route path="mileage" element={<FinanceMileage />} />
                <Route path="reports" element={<FinanceReports />} />
                <Route path="ai-assistant" element={<FinanceAIAssistant />} />
                <Route path="ai-tools" element={<FinanceAITools />} />
                <Route path="users" element={<FinanceUsers />} />
                <Route path="settings" element={<FinanceSettings />} />
                <Route path="import" element={<FinanceImportData />} />
              </Route>
              
              {/* MyHR Orbit - Fully Integrated */}
              <Route path="/suite/hr" element={<HRLayout />}>
                <Route index element={<HRDashboardWrapper />} />
                {/* Employee Self-Service Routes */}
                <Route path="my-leave" element={<HRLeaveManagement employeeView={true} />} />
                <Route path="my-attendance" element={<HRAttendance employeeView={true} />} />
                <Route path="my-claims" element={<HRClaims employeeView={true} />} />
                <Route path="my-payslips" element={<HRPayslips employeeView={true} />} />
                <Route path="my-profile" element={<MyProfile />} />
                {/* Admin Routes */}
                <Route path="onboarding" element={<OnboardingDashboard />} />
                <Route path="offer-templates" element={<OfferTemplates />} />
                <Route path="employees" element={<HREmployees />} />
                <Route path="employees/:id" element={<EmployeeDetails />} />
                <Route path="employees/:id/edit" element={<EmployeeEdit />} />
                <Route path="leave" element={<HRLeaveManagement />} />
                <Route path="attendance" element={<HRAttendance />} />
                <Route path="claims" element={<HRClaims />} />
                <Route path="payroll" element={<HRPayroll />} />
                <Route path="payslips" element={<HRPayslips />} />
                <Route path="performance" element={<HRPerformance />} />
                <Route path="reports" element={<HRReports />} />
                <Route path="forms" element={<HRForms />} />
                <Route path="import" element={<HRImportData />} />
                <Route path="settings" element={<HRSettings />} />
                <Route path="documents" element={<HRDocuments />} />
                <Route path="sign-document/:documentId/:token" element={<SignDocument />} />
                <Route path="profile" element={<MyProfile />} />
              </Route>
              
              {/* Public Signing Route (no auth required) */}
              <Route path="/sign/:documentId/:token" element={<PublicSign />} />
              
              {/* MyDoc Orbit - Fully Integrated */}
              <Route path="/suite/docs" element={<DocsLayout />}>
                <Route index element={<DocsDashboard />} />
                <Route path="chat" element={<DocsChatPage />} />
                <Route path="documents" element={<DocsDocumentsPage />} />
                <Route path="compare" element={<DocsComparePage />} />
                <Route path="batch" element={<DocsBatchAnalysisPage />} />
                <Route path="reports" element={<DocsReportsPage />} />
                <Route path="users" element={<DocsUserManagementPage />} />
                <Route path="audit" element={<DocsAuditLogPage />} />
                <Route path="sso" element={<DocsSSOConfigPage />} />
                <Route path="licenses" element={<DocsLicenseManagementPage />} />
                <Route path="subscriptions" element={<DocsLicenseManagementPage />} />
                <Route path="queries" element={<DocsQueryManagementPage />} />
              </Route>
              
              {/* Document Signing Page - Public Route */}
              <Route path="/sign/:token" element={<SignOfferPage />} />
              
              {/* Policy Pages */}
              <Route path="/policy/:policyId" element={<PolicyPage />} />
              
              {/* Sales Deck */}
              <Route path="/sales-deck" element={<SalesDeck />} />
              
              {/* Admin Dashboard Routes */}
              <Route path="/admin" element={<AdminLayout><Dashboard /></AdminLayout>} />
              <Route path="/admin/all-jobs" element={<AdminLayout><AllJobs /></AdminLayout>} />
              <Route path="/admin/applied-jobs" element={<AdminLayout><AppliedJobs /></AdminLayout>} />
              <Route path="/admin/add-job" element={<AdminLayout><AddNewJob /></AdminLayout>} />
              <Route path="/admin/edit-job/:id" element={<AdminLayout><AddNewJob /></AdminLayout>} />
              <Route path="/admin/removed-jobs" element={<AdminLayout><RemovedJobs /></AdminLayout>} />
              <Route path="/admin/enquiries" element={<AdminLayout><Enquiries /></AdminLayout>} />
              <Route path="/admin/live-chat" element={<AdminLayout><LiveChat /></AdminLayout>} />
              <Route path="/admin/talents" element={<AdminLayout><TalentManagement /></AdminLayout>} />
              <Route path="/admin/projects" element={<AdminLayout><Projects /></AdminLayout>} />
              <Route path="/admin/projects/add" element={<AddProject />} />
              <Route path="/admin/projects/edit/:projectId" element={<AddProject />} />
              <Route path="/admin/blogs" element={<AdminLayout><Blogs /></AdminLayout>} />
              <Route path="/admin/users" element={<AdminLayout><UserManagement /></AdminLayout>} />
              <Route path="/admin/clients" element={<AdminLayout><ClientManagement /></AdminLayout>} />
              <Route path="/admin/branding" element={<AdminLayout><TenantBrandingPage /></AdminLayout>} />
              <Route path="/admin/analytics" element={<AdminLayout><Analytics /></AdminLayout>} />
              
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Chatbot />
          <Toaster position="bottom-right" richColors />
        </div>
      </PushNotificationProvider>
      </ChatbotProvider>
    </ThemeProvider>
  </BrandingProvider>
  );
}

export default App;
